import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Divider,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Receipt as ReceiptIcon,
  Image as ImageIcon,
  Inventory as InventoryIcon,
  MoveDown as MoveDownIcon,
  GridView as GridViewIcon,
  WarningAmber as WarningAmberIcon,
  RemoveShoppingCart as RemoveShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { useCallback, useMemo } from 'react';
import StorefrontProductSearch from './StorefrontProductSearch';
import SalesHistoryTab from '../components/SalesHistoryTab';

// Custom date formatter
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString();
};

// Calculate time remaining for a reservation
const getTimeRemaining = (expiresAt) => {
  if (!expiresAt) return { expired: true, text: 'N/A' };

  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry - now;

  if (diffMs <= 0) return { expired: true, text: 'Expired' };

  const diffMins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;

  return {
    expired: false,
    text: `${hours}h ${mins}m remaining`,
    hours,
    minutes: mins
  };
};

// Calculate savings percentage
const calculateSavingsPercent = (originalPrice, discountPrice) => {
  const original = parseFloat(originalPrice);
  const discount = parseFloat(discountPrice);
  return Math.round(((original - discount) / original) * 100);
};

const OutletManager = () => {
  // State for outlet items
  const [outletItems, setOutletItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // State for UI — tabs: 0 = Sales History (default), 1 = Outlet Item
  const [tabValue, setTabValue] = useState(0);
  const [message, setMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [messageType, setMessageType] = useState('success');

  // State for dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [divertDialogOpen, setDivertDialogOpen] = useState(false);
  const [divertQuantity, setDivertQuantity] = useState(1);
  const [divertOrderId, setDivertOrderId] = useState('');
  const [divertFulfill, setDivertFulfill] = useState(true);
  const [divertMatches, setDivertMatches] = useState([]);
  const [divertMatchesLoading, setDivertMatchesLoading] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editQuantity, setEditQuantity] = useState(1);
  const [salesHistory, setSalesHistory] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);

  // State for image correction dialog
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageItemId, setImageItemId] = useState('');
  const [correctingImages, setCorrectingImages] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [hiddenOrders, setHiddenOrders] = useState([]);
  const [salesHistorySearch, setSalesHistorySearch] = useState('');
  const [filteredSalesHistory, setFilteredSalesHistory] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [productMetadata, setProductMetadata] = useState({});

  // Shelf-bin (貨架格) editing
  const [binDialogOpen, setBinDialogOpen] = useState(false);
  const [binValue, setBinValue] = useState('');
  const [savingBin, setSavingBin] = useState(false);

  // 依貨架格 view (on-shelf items grouped by bin)
  const [binView, setBinView] = useState([]);
  const [loadingBins, setLoadingBins] = useState(false);
  const [binSearch, setBinSearch] = useState('');

  // 待處理 (reconcile) tab: ghost units still on the shelf but hidden from the
  // store (dead orders), + garments diverted to storefront awaiting a physical
  // move to the receiving rack.
  const [reconcileItems, setReconcileItems] = useState([]);
  const [diversionMoves, setDiversionMoves] = useState([]);
  const [loadingReconcile, setLoadingReconcile] = useState(false);
  const [relistingKey, setRelistingKey] = useState(null);
  const [retiringKey, setRetiringKey] = useState(null);
  const [movingId, setMovingId] = useState(null);
  const [showMovedDiversions, setShowMovedDiversions] = useState(false);
  // Bulk re-discount (再打折) over every listed outlet item. `discountPreview`
  // holds the server's dry-run result and is cleared whenever an input changes,
  // so 套用 can only ever apply a set the admin has actually seen.
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountPercent, setDiscountPercent] = useState('15');
  const [discountFloor, setDiscountFloor] = useState('250');
  const [discountPreview, setDiscountPreview] = useState(null);
  const [discountBusy, setDiscountBusy] = useState(false);

  // Write-off (架上找不到) quantity dialog
  const [writeOffItem, setWriteOffItem] = useState(null);
  const [writeOffQty, setWriteOffQty] = useState(1);
  const [writingOff, setWritingOff] = useState(false);

  // Load outlet items on initial render
  useEffect(() => {
    fetchOutletItems();

    // Set up refresh interval for time remaining displays
    const intervalId = setInterval(() => {
      if (outletItems.length > 0) {
        setOutletItems(prevItems => [...prevItems]);
      }
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  // Handle tab change — 0 = Sales History (default), 1 = Outlet Item
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 0) {
      fetchSalesHistory();
    }
    if (newValue === 1 && outletItems.length === 0) {
      fetchOutletItems();
    }
    if (newValue === 2) {
      fetchBinView();
    }
    if (newValue === 3) {
      fetchReconcile();
    }
  };



  useEffect(() => {
    const fetchProductMetadata = async () => {
      try {
        console.log('Fetching product metadata...');
        const response = await api.get('/api/products/metadata');
        const metadata = response.data || [];

        console.log(`Fetched ${metadata.length} product metadata entries`);

        const metadataDict = metadata.reduce((acc, item) => {
          const key = item.product_key;
          if (key) {
            acc[key] = {
              product_key: key,
              tag: item.tag || '',
              reference_image: item.reference_image || undefined,
              referenceImage: item.reference_image || undefined,
              createdAt: item.created_at || new Date().toISOString(),
              lastUpdated: item.last_updated || new Date().toISOString()
            };
          }
          return acc;
        }, {});

        setProductMetadata(metadataDict);
      } catch (error) {
        console.error('Failed to fetch product metadata:', error);
      }
    };

    fetchProductMetadata();
  }, []);

  const forceRefreshSalesData = useCallback(async () => {
    try {
      console.log('🔄 Force refreshing sales data...');
      const response = await api.get(`/api/outlet/admin/sales?showHidden=${showHidden}`);
      setSalesHistory(response.data.sales || []);
      setHiddenOrders(response.data.hiddenOrderIds || []);
      console.log('✅ Force refresh completed');
    } catch (error) {
      console.error('❌ Error force refreshing sales:', error);
    }
  }, [showHidden]);

  const toggleHideOrder = async (orderId, source, isCurrentlyHidden) => {
    try {
      // Call the appropriate API based on current hidden status
      if (isCurrentlyHidden) {
        await api.post(`/api/outlet/admin/orders/${orderId}/show`);
        showMessage('Order is now visible', 'success');
      } else {
        await api.post(`/api/outlet/admin/orders/${orderId}/hide`, { source });
        showMessage('Order hidden successfully', 'success');
      }

      // Update local state to avoid a full refetch
      if (isCurrentlyHidden) {
        setHiddenOrders(prev => prev.filter(id => id !== orderId));
      } else {
        setHiddenOrders(prev => [...prev, orderId]);
      }

      // Update filtered list
      setFilteredSalesHistory(prev => {
        if (!showHidden && !isCurrentlyHidden) {
          // If we're not showing hidden items and we just hid one, remove it
          return prev.filter(sale => sale.order_id !== orderId);
        }
        return prev.map(sale =>
          sale.order_id === orderId
            ? { ...sale, is_hidden: !isCurrentlyHidden }
            : sale
        );
      });
    } catch (error) {
      console.error('Error toggling order visibility:', error);
      showMessage('Failed to update order visibility', 'error');
    }
  };

  const toggleReceivedStatus = async (orderId, source, isCurrentlyReceived) => {
    try {
      if (isCurrentlyReceived) {
        await api.post(`/api/outlet/admin/orders/${orderId}/unreceived`);
        showMessage('Order marked as not received', 'success');
      } else {
        await api.post(`/api/outlet/admin/orders/${orderId}/received`, { source });
        showMessage('Order marked as received', 'success');
      }

      // Update local state to avoid a full refetch
      setFilteredSalesHistory(prev =>
        prev.map(sale =>
          sale.order_id === orderId
            ? { ...sale, is_received: !isCurrentlyReceived }
            : sale
        )
      );
    } catch (error) {
      console.error('Error updating received status:', error);
      showMessage('Failed to update received status', 'error');
    }
  };

  const toggleSentStatus = async (orderId, source, isCurrentlySent) => {
    try {
      if (isCurrentlySent) {
        await api.post(`/api/outlet/admin/orders/${orderId}/unsent`);
        showMessage('Order marked as not sent', 'success');
      } else {
        await api.post(`/api/outlet/admin/orders/${orderId}/sent`, { source });
        showMessage('Order marked as sent', 'success');
      }

      // Update local state to avoid a full refetch
      setFilteredSalesHistory(prev =>
        prev.map(sale =>
          sale.order_id === orderId
            ? { ...sale, is_sent: !isCurrentlySent }
            : sale
        )
      );
    } catch (error) {
      console.error('Error updating sent status:', error);
      showMessage('Failed to update sent status', 'error');
    }
  };

  // Show a message to the user
  const showMessage = (msg, type = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setSnackbarOpen(true);
  };

  // Close the snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Fetch outlet items from API
  const fetchOutletItems = useCallback(async () => {
    // Don't set loading if we already have items (to prevent flash on refresh)
    const showLoading = outletItems.length === 0;

    try {
      if (showLoading) setLoadingItems(true);
      const response = await api.get('/api/outlet/admin/items');

      // Only update if we have items or if we didn't have any before
      if (response.data && (response.data.length > 0 || outletItems.length === 0)) {
        setOutletItems(response.data);
      }
    } catch (error) {
      console.error('Error fetching outlet items:', error);
      // Only show error message if we don't have any items
      if (outletItems.length === 0) {
        showMessage('Failed to fetch outlet items', 'error');
      }
    } finally {
      if (showLoading) setLoadingItems(false);
    }
  }, [outletItems.length]);

  // 依貨架格 view: fetch on-shelf items (with available vs 待出貨 breakdown).
  const fetchBinView = useCallback(async () => {
    try {
      setLoadingBins(true);
      const resp = await api.get('/api/outlet/admin/bins');
      setBinView(resp.data?.items || []);
    } catch (error) {
      console.error('Error fetching bin view:', error);
      showMessage('載入貨架格失敗', 'error');
    } finally {
      setLoadingBins(false);
    }
  }, []);

  // 待處理: fetch ghost units (shelf reconciliation) + diverted-garment moves.
  // includeMoved reveals already-moved rows so an accidental check can be undone.
  const fetchReconcile = useCallback(async (includeMoved = showMovedDiversions) => {
    try {
      setLoadingReconcile(true);
      const [rec, mv] = await Promise.all([
        api.get('/api/outlet/admin/shelf-reconcile'),
        api.get('/api/outlet/admin/diversion-moves', { params: includeMoved ? { includeMoved: 1 } : {} }),
      ]);
      setReconcileItems(rec.data?.items || []);
      setDiversionMoves(mv.data?.moves || []);
    } catch (error) {
      console.error('Error fetching reconcile data:', error);
      showMessage('載入待處理清單失敗', 'error');
    } finally {
      setLoadingReconcile(false);
    }
  }, [showMovedDiversions]);

  // Relist one ghost unit: void the dead order's unshipped outlet sale so the
  // garment returns to the sellable list and keeps its bin, then hide the order.
  const handleRelistGhost = async (row) => {
    const key = `${row.order_id}-${row.outlet_item_id}`;
    if (!window.confirm(
      `確定把「${row.display_sku || row.sku} ${row.variant_details || ''}」退回架上？\n\n` +
      `這會取消訂單 ${row.order_id} 對這件的佔用（未出貨），讓它重新上架販售並保留貨架格，` +
      `並把這筆無效訂單隱藏。`
    )) return;
    try {
      setRelistingKey(key);
      const resp = await api.post(`/api/outlet/admin/orders/${row.order_id}/relist`, {
        itemId: row.outlet_item_id,
        hideOrder: true,
      });
      showMessage(resp.data?.message || '已退回架上', 'success');
      fetchReconcile();
    } catch (error) {
      console.error('Relist failed:', error);
      showMessage(error.response?.data?.message || '退回架上失敗', 'error');
    } finally {
      setRelistingKey(null);
    }
  };

  // Retire one ghost unit WITHOUT relisting — the garment is GONE (not on the
  // shelf). Marks the dead order's unshipped sale as shipped so it drops off the
  // list and frees its bin, but does NOT put it back on sale.
  const handleRetireGhost = async (row) => {
    const key = `${row.order_id}-${row.outlet_item_id}`;
    if (!window.confirm(
      `確認「${row.display_sku || row.sku} ${row.variant_details || ''}」缺件（實體已不在架上）？\n\n` +
      `這會把它從幽靈清單移除並釋放貨架格，但「不會」重新上架販售。`
    )) return;
    try {
      setRetiringKey(key);
      const resp = await api.post(`/api/outlet/admin/orders/${row.order_id}/retire-ghost`, {
        itemId: row.outlet_item_id,
      });
      showMessage(resp.data?.message || '已移除', 'success');
      fetchReconcile();
    } catch (error) {
      console.error('Retire ghost failed:', error);
      showMessage(error.response?.data?.message || '移除失敗', 'error');
    } finally {
      setRetiringKey(null);
    }
  };

  // Write off available units that physically are NOT on the shelf (盤損): the bin
  // says the garment is here but it isn't. Opens a quantity dialog so the operator
  // can remove some (e.g. 可售 3 → 1) or all. Only the 可售 (available) units can be
  // written off — 待出貨 (sold, unshipped) units belong to a customer.
  const handleWriteOffMissing = (it) => {
    const avail = it.available_count ?? 0;
    if (avail <= 0) {
      showMessage('此格沒有可移除的可售單位（待出貨屬於客人訂單）', 'warning');
      return;
    }
    setWriteOffItem(it);
    setWriteOffQty(avail); // default: remove all missing
  };

  const confirmWriteOff = async () => {
    if (!writeOffItem) return;
    const avail = writeOffItem.available_count ?? 0;
    const qty = Math.max(1, Math.min(avail, parseInt(writeOffQty, 10) || avail));
    try {
      setWritingOff(true);
      const resp = await api.post(`/api/outlet/admin/items/${writeOffItem.id}/write-off`, { quantity: qty });
      showMessage(resp.data?.message || '已移除', 'success');
      setWriteOffItem(null);
      fetchBinView();
    } catch (error) {
      console.error('Write-off failed:', error);
      showMessage(error.response?.data?.message || '移除失敗', 'error');
    } finally {
      setWritingOff(false);
    }
  };

  // Toggle a diverted garment's "moved to receiving rack" checkmark. moved=true
  // checks it (drops off the pending list); moved=false un-checks it (restores it),
  // so an accidental check can be undone from the 顯示已搬移 view.
  const handleMarkMoved = async (row, moved) => {
    try {
      setMovingId(row.id);
      await api.post(`/api/outlet/admin/diversion-moves/${row.id}/moved`, { moved });
      showMessage(moved ? '已標記為已搬移' : '已取消標記', 'success');
      fetchReconcile();
    } catch (error) {
      console.error('Mark moved failed:', error);
      showMessage(error.response?.data?.message || '標記失敗', 'error');
    } finally {
      setMovingId(null);
    }
  };

  // Toggle showing already-moved diversions (for undo), and reload with the flag.
  const toggleShowMovedDiversions = () => {
    const next = !showMovedDiversions;
    setShowMovedDiversions(next);
    fetchReconcile(next);
  };

  // Group on-shelf items into bins, sorted by bin number.
  const binGroups = useMemo(() => {
    const map = new Map();
    (binView || []).forEach((it) => {
      const k = it.slot_number;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    });
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [binView]);

  // Filter the bin view by tag / title / SKU / variant — so you can find which
  // bin a tag already lives in before shelving a new item. Only bins containing
  // a matching item are kept, and within each only the matching items are shown.
  const filteredBinGroups = useMemo(() => {
    const q = binSearch.trim().toLowerCase();
    if (!q) return binGroups;
    const map = new Map();
    (binView || []).forEach((it) => {
      const fullSku = it.display_sku || it.sku || '';
      const skuNumber = fullSku.split('(')[0].split(' ')[0].trim();
      const tag = (skuNumber && productMetadata[skuNumber]?.tag) || '';
      const hay = `${it.title || ''} ${fullSku} ${it.variant_details || ''} ${tag}`.toLowerCase();
      if (!hay.includes(q)) return;
      const k = it.slot_number;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    });
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [binView, binGroups, binSearch, productMetadata]);

  // Count of still-listed items that don't have a shelf-bin yet — drives the
  // "分配貨架格" button. Backfill is explicit (not automatic) because the catalog
  // is large (~1000 items): the operator decides when to bulk-assign and tunes
  // OUTLET_BIN_COUNT / OUTLET_BIN_TARGET to their real physical bins first.
  const unbinnedCount = outletItems.filter(
    (it) => it.slot_number == null &&
      (it.available_count ?? it.available_quantity ?? 0) > 0
  ).length;

  // 再打折 — ask the server what a blanket discount WOULD do. Writes nothing;
  // the response drives the preview table.
  const openDiscountDialog = () => {
    setDiscountPreview(null);
    setDiscountDialogOpen(true);
  };

  const runDiscountPreview = async () => {
    try {
      setDiscountBusy(true);
      const resp = await api.post('/api/outlet/admin/items/bulk-discount', {
        percentOff: Number(discountPercent),
        floor: Number(discountFloor || 0),
        dryRun: true,
      });
      setDiscountPreview(resp.data);
      if (!resp.data?.matched) {
        showMessage('沒有符合條件的商品（可能都已到最低價）', 'info');
      }
    } catch (error) {
      console.error('Outlet bulk-discount preview failed:', error);
      showMessage(error.response?.data?.message || '試算失敗', 'error');
    } finally {
      setDiscountBusy(false);
    }
  };

  // Applies the previewed discount. The server recomputes the set from scratch,
  // so a row that sold out between 試算 and 套用 simply drops out.
  const applyBulkDiscount = async () => {
    if (!discountPreview?.matched) return;
    const ok = window.confirm(
      `確定把 ${discountPreview.matched} 件出清商品「再打 ${discountPercent}% 折」（最低 NT$${discountFloor}）？\n\n` +
      '前台售價會立即改變，且無法一鍵還原。'
    );
    if (!ok) return;
    try {
      setDiscountBusy(true);
      const resp = await api.post('/api/outlet/admin/items/bulk-discount', {
        percentOff: Number(discountPercent),
        floor: Number(discountFloor || 0),
        dryRun: false,
      });
      showMessage(`已調降 ${resp.data?.updated ?? 0} 件出清商品售價`, 'success');
      setDiscountDialogOpen(false);
      setDiscountPreview(null);
      fetchOutletItems();
    } catch (error) {
      console.error('Outlet bulk-discount failed:', error);
      showMessage(error.response?.data?.message || '再打折失敗', 'error');
    } finally {
      setDiscountBusy(false);
    }
  };

  // Assign bins to every listed item missing one (server packs compactly).
  const handleBackfillBins = async () => {
    try {
      setLoadingItems(true);
      const resp = await api.post('/api/outlet/admin/slots/backfill');
      showMessage(`已分配 ${resp.data?.assigned ?? 0} 項貨架格`, 'success');
      fetchOutletItems();
    } catch (error) {
      console.error('Outlet bin backfill failed:', error);
      showMessage(error.response?.data?.message || '分配貨架格失敗', 'error');
    } finally {
      setLoadingItems(false);
    }
  };

  // Clear all bins (incl. stale phantom-row bins) and re-pack listed items into
  // the fewest bins. Cleanup for an over-binned pool.
  const handleRebalanceBins = async () => {
    // DESTRUCTIVE: rebalance clears EVERY bin number and re-packs from bin 1, so
    // the physical rack must be fully re-shelved to match. Guard against an
    // accidental click that would silently invalidate every bin on the shelf.
    const ok = window.confirm(
      '⚠️ 重新整理貨架格會「清除所有格號並從 1 號重新編排」。\n\n' +
      '這代表資料庫裡每一件的格號都會改變，你必須「把整個貨架依新格號重新排列」，' +
      '否則系統格號會跟實體位置對不上。\n\n' +
      '只有在你打算整架重新上架時才使用。確定要繼續嗎？'
    );
    if (!ok) return;
    try {
      setLoadingItems(true);
      const resp = await api.post('/api/outlet/admin/slots/rebalance');
      showMessage(`已重新整理：${resp.data?.assigned ?? 0} 項 → ${resp.data?.bins ?? 0} 格`, 'success');
      fetchOutletItems();
    } catch (error) {
      console.error('Outlet bin rebalance failed:', error);
      showMessage(error.response?.data?.message || '重新整理貨架格失敗', 'error');
    } finally {
      setLoadingItems(false);
    }
  };

  // Add these functions for optimistic updates
  const updateSaleInList = useCallback((orderId, updates) => {
    console.log('🔄 updateSaleInList called:', { orderId, updates });

    setSalesHistory(prevSales => {
      const updated = prevSales.map(sale =>
        sale.order_id === orderId
          ? { ...sale, ...updates }
          : sale
      );
      console.log('📊 Updated salesHistory for order:', orderId);
      return updated;
    });
  }, []);

  const addToHiddenOrders = (orderId) => {
    setHiddenOrders(prev => {
      if (!prev.includes(orderId)) {
        return [...prev, orderId];
      }
      return prev;
    });
  };

  const removeFromHiddenOrders = (orderId) => {
    setHiddenOrders(prev => prev.filter(id => id !== orderId));
  };


  // Client-side filtering for search only (hidden filtering is handled by the backend)
  // When a search matches any item in an order, show ALL items in that order
  useEffect(() => {
    if (salesHistory.length > 0) {
      if (salesHistorySearch) {
        const query = salesHistorySearch.toLowerCase();
        const saleMatchesQuery = (sale) => {
          if (sale.order_id.toString().includes(query)) return true;
          if (sale.title && sale.title.toLowerCase().includes(query)) return true;
          if (sale.customer_phone && sale.customer_phone.includes(query)) return true;
          if (sale.customer_email && sale.customer_email.toLowerCase().includes(query)) return true;
          if (sale.sku && sale.sku.toLowerCase().includes(query)) return true;
          if (sale.source && sale.source.toLowerCase().includes(query)) return true;
          // Search by product tag (from productMetadata, keyed by SKU number)
          const fullSku = sale.display_sku || sale.sku || '';
          const skuNumber = fullSku.split('(')[0].split(' ')[0].trim();
          if (skuNumber && productMetadata[skuNumber]) {
            const tag = productMetadata[skuNumber].tag || '';
            if (tag.toLowerCase().includes(query)) return true;
          }
          return false;
        };
        // First pass: collect order_ids where any item matches
        const matchingOrderIds = new Set();
        salesHistory.forEach(sale => {
          if (saleMatchesQuery(sale)) {
            matchingOrderIds.add(sale.order_id);
          }
        });
        // Second pass: include ALL items from matching orders
        const filtered = salesHistory.filter(sale => matchingOrderIds.has(sale.order_id));
        setFilteredSalesHistory(filtered);
      } else {
        setFilteredSalesHistory(salesHistory);
      }
    } else {
      setFilteredSalesHistory([]);
    }
  }, [salesHistory, salesHistorySearch, productMetadata]);

  // Add a force refresh function if optimistic updates fail


  // Updated fetchSalesHistory to preserve scroll position
  const fetchSalesHistory = useCallback(async () => {
    try {
      // Store current scroll position
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      setLoadingSales(true);
      const response = await api.get(`/api/outlet/admin/sales?showHidden=${showHidden}`);
      setSalesHistory(response.data.sales || []);
      setHiddenOrders(response.data.hiddenOrderIds || []);

      // Restore scroll position after a short delay
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);

    } catch (error) {
      console.error('Error fetching sales history:', error);
      showMessage('Failed to fetch sales history', 'error');
    } finally {
      setLoadingSales(false);
    }
  }, [showHidden]);

  // Re-fetch sales history when showHidden toggles
  useEffect(() => {
    fetchSalesHistory();
  }, [showHidden, fetchSalesHistory]);

  // Add this function to refresh just specific order data without scroll reset
  const refreshSpecificOrder = useCallback(async (orderId) => {
    try {
      console.log('🎯 Refreshing specific order:', orderId);

      // Try to get updated order data from the API
      const response = await api.get(`/api/outlet/admin/orders/${orderId}/details`);

      if (response.data && response.data.success) {
        const orderItems = response.data.orderItems || [response.data.order];

        // Update each item for this order
        orderItems.forEach((updatedItem) => {
          if (updatedItem.order_id) {
            updateSaleInList(updatedItem.order_id, updatedItem);
          }
        });

        console.log('✅ Successfully refreshed order:', orderId);
        return true;
      }
    } catch (error) {
      console.error('❌ Error refreshing specific order:', error);

      // Fallback: do a minimal refresh without scroll reset
      try {
        const response = await api.get(`/api/outlet/admin/sales?orderId=${orderId}&showHidden=${showHidden}`);
        if (response.data && response.data.sales) {
          const updatedOrderItems = response.data.sales.filter(sale => sale.order_id === orderId);
          updatedOrderItems.forEach(item => {
            updateSaleInList(orderId, item);
          });
        }
      } catch (fallbackError) {
        console.error('❌ Fallback refresh also failed:', fallbackError);
      }
    }

    return false;
  }, [updateSaleInList, showHidden]);

  // Handle releasing all expired reservations
  const handleReleaseExpired = async () => {
    try {
      setLoadingItems(true);
      const response = await api.post('/api/outlet/release-expired');
      showMessage('Expired reservations released successfully', 'success');
      fetchOutletItems();
    } catch (error) {
      console.error('Error releasing expired reservations:', error);
      showMessage('Failed to release expired reservations', 'error');
    } finally {
      setLoadingItems(false);
    }
  };

  // Open edit dialog
  const handleOpenEditDialog = (item) => {
    setCurrentItem(item);
    setEditPrice(item.discount_price);
    setEditQuantity(item.available_quantity);
    setEditDialogOpen(true);
  };

  // NEW: Open image correction dialog
  const handleOpenImageDialog = (item) => {
    setCurrentItem(item);
    setImageItemId(''); // Clear previous input
    setImageDialogOpen(true);
  };

  // NEW: Preview images
  const handlePreviewImages = async (productId, shopeeItemId) => {
    try {
      setCorrectingImages(true);

      // First fetch the images to preview them
      const response = await api.get(`/api/shopee/products/${shopeeItemId}/details-for-import`);

      let images = [];

      // Extract images from response
      if (response.data?.success && response.data?.enhanced_data) {
        const data = response.data.enhanced_data;

        if (data.image && data.image.image_url_list && Array.isArray(data.image.image_url_list)) {
          // Transform to the format needed for preview
          images = data.image.image_url_list.map((url, index) => ({
            id: index + 1,
            src: url,
            position: index + 1
          }));
        } else if (data.image_url) {
          // Single image
          images = [{
            id: 1,
            src: data.image_url,
            position: 1
          }];
        }
      }

      if (images.length === 0) {
        showMessage('No images found for this Shopee item ID', 'warning');
        return;
      }

      // Show preview images inline within the image correction dialog
      setPreviewImages(images);
    } catch (error) {
      console.error('Error previewing images:', error);
      showMessage('Failed to fetch images for preview', 'error');
    } finally {
      setCorrectingImages(false);
    }
  };

  // NEW: Handle image correction
  const handleCorrectImages = async () => {
    if (!imageItemId || !currentItem) {
      showMessage('Please enter a valid Shopee item ID', 'warning');
      return;
    }

    try {
      setCorrectingImages(true);

      const response = await api.post('/api/outlet/admin/update-images', {
        productId: currentItem.id,
        shopeeItemId: imageItemId
      });

      console.log('Full image update response:', response.data);

      // Optional: Log original product data if available
      if (response.data.originalProductData) {
        console.log('Original Product Data:', response.data.originalProductData);
      }

      setImageDialogOpen(false);
      showMessage('Product images updated successfully', 'success');
      fetchOutletItems();
    } catch (error) {
      console.error('Error updating product images:', error.response || error);

      // More detailed error logging
      if (error.response) {
        console.error('Error details:', {
          data: error.response.data,
          status: error.response.status,
          headers: error.response.headers
        });
      }

      showMessage(
        error.response?.data?.message ||
        error.message ||
        'Failed to update images',
        'error'
      );
    } finally {
      setCorrectingImages(false);
    }
  };

  // Handle updating an outlet item
  const handleUpdateItem = async () => {
    try {
      setLoadingItems(true);

      const response = await api.put(`/api/outlet/admin/items/${currentItem.id}`, {
        discount_price: parseFloat(editPrice),
        available_quantity: parseInt(editQuantity)
      });

      setEditDialogOpen(false);
      showMessage('Item updated successfully', 'success');
      fetchOutletItems();
    } catch (error) {
      console.error('Error updating outlet item:', error);
      showMessage(error.response?.data?.message || 'Failed to update item', 'error');
    } finally {
      setLoadingItems(false);
    }
  };

  // Open the shelf-bin (貨架格) editor for an item
  const handleOpenBinDialog = (item) => {
    setCurrentItem(item);
    setBinValue(item.slot_number != null ? String(item.slot_number) : '');
    setBinDialogOpen(true);
  };

  // Save a manual bin override (or clear it when left empty)
  const handleSaveBin = async () => {
    if (!currentItem) return;
    try {
      setSavingBin(true);
      await api.patch(`/api/outlet/admin/items/${currentItem.id}/slot`, {
        slot: binValue === '' ? null : parseInt(binValue, 10),
      });
      setBinDialogOpen(false);
      showMessage('貨架格已更新', 'success');
      fetchOutletItems();
    } catch (error) {
      console.error('Error updating bin:', error);
      showMessage(error.response?.data?.message || '更新貨架格失敗', 'error');
    } finally {
      setSavingBin(false);
    }
  };

  // Let the system pick the next free bin (compact packing)
  const handleAutoBin = async () => {
    if (!currentItem) return;
    try {
      setSavingBin(true);
      const resp = await api.patch(`/api/outlet/admin/items/${currentItem.id}/slot`, { auto: true });
      setBinDialogOpen(false);
      showMessage(`已自動分配至 ${resp.data?.slot} 號格`, 'success');
      fetchOutletItems();
    } catch (error) {
      console.error('Error auto-assigning bin:', error);
      showMessage(error.response?.data?.message || '自動分配失敗', 'error');
    } finally {
      setSavingBin(false);
    }
  };

  // Open delete dialog
  const handleOpenDeleteDialog = (item) => {
    setCurrentItem(item);
    setDeleteDialogOpen(true);
  };

  // Handle deleting an outlet item
  const handleDeleteItem = async () => {
    try {
      setLoadingItems(true);

      const response = await api.delete(`/api/outlet/admin/items/${currentItem.id}`);

      setDeleteDialogOpen(false);
      showMessage('Item deleted successfully', 'success');
      fetchOutletItems();
    } catch (error) {
      console.error('Error deleting outlet item:', error);
      showMessage(error.response?.data?.message || 'Failed to delete item', 'error');
    } finally {
      setLoadingItems(false);
    }
  };

  // Open divert-to-storefront dialog
  const handleOpenDivertDialog = async (item) => {
    setCurrentItem(item);
    setDivertQuantity(1);
    setDivertOrderId('');
    // Default to auto-fulfil when there are pending storefront orders waiting
    setDivertFulfill((item.pending_order_count || 0) > 0);
    setDivertMatches([]);
    setDivertDialogOpen(true);

    // Load the oldest pending orders so we can preview exactly which ones a
    // batch divert will fill.
    if ((item.pending_order_count || 0) > 0) {
      try {
        setDivertMatchesLoading(true);
        const resp = await api.get(`/api/outlet/admin/items/${item.id}/pending-matches`, {
          params: { limit: 50 },
        });
        setDivertMatches(resp.data?.matches || []);
      } catch (error) {
        console.error('Error loading pending matches:', error);
      } finally {
        setDivertMatchesLoading(false);
      }
    }
  };

  // Handle diverting stock to a storefront order (lowers stock, keeps history)
  const handleDivertItem = async () => {
    try {
      setLoadingItems(true);
      const resp = await api.post(`/api/outlet/admin/items/${currentItem.id}/divert`, {
        quantity: parseInt(divertQuantity) || 1,
        orderId: divertOrderId.trim() || null,
        fulfillOldest: divertFulfill,
      });
      setDivertDialogOpen(false);
      const fulfilled = resp.data?.fulfilled || [];
      showMessage(
        fulfilled.length
          ? `已撥給 ${fulfilled.length} 張訂單，請放到：${fulfilled.map((f) => `${f.orderId} → ${f.receivingBin != null ? `收貨${f.receivingBin}號格` : '直接出貨'}`).join('、')}`
          : 'Stock diverted to storefront — outlet listing updated',
        'success'
      );
      fetchOutletItems();
    } catch (error) {
      console.error('Error diverting outlet item:', error);
      showMessage(error.response?.data?.message || 'Failed to divert stock', 'error');
    } finally {
      setLoadingItems(false);
    }
  };

  // Open release reservation dialog
  const handleOpenReleaseDialog = (item) => {
    setCurrentItem(item);
    setReleaseDialogOpen(true);
  };

  // Handle releasing a reservation
  const handleReleaseReservation = async () => {
    try {
      setLoadingItems(true);

      const response = await api.post(`/api/outlet/admin/items/${currentItem.id}/release`);

      setReleaseDialogOpen(false);
      showMessage('Reservation released successfully', 'success');
      fetchOutletItems();
    } catch (error) {
      console.error('Error releasing reservation:', error);
      showMessage(error.response?.data?.message || 'Failed to release reservation', 'error');
    } finally {
      setLoadingItems(false);
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="outlet management tabs"
        >
          <Tab label="Sales History" icon={<ReceiptIcon />} iconPosition="start" />
          <Tab label="Outlet Item" icon={<InventoryIcon />} iconPosition="start" />
          <Tab label="依貨架格" icon={<GridViewIcon />} iconPosition="start" />
          <Tab label="待處理" icon={<WarningAmberIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab 0: Sales History (default) */}
      {tabValue === 0 && (
        <SalesHistoryTab
          salesHistory={salesHistory}
          loadingSales={loadingSales}
          hiddenOrders={hiddenOrders}
          showHidden={showHidden}
          setSalesHistorySearch={setSalesHistorySearch}
          salesHistorySearch={salesHistorySearch}
          fetchSalesHistory={fetchSalesHistory}
          showMessage={showMessage}
          setShowHidden={setShowHidden}
          filteredSalesHistory={filteredSalesHistory}
          updateSaleInList={updateSaleInList}
          addToHiddenOrders={addToHiddenOrders}
          removeFromHiddenOrders={removeFromHiddenOrders}
          refreshSpecificOrder={refreshSpecificOrder}
          productMetadata={productMetadata}
        />
      )}

      {/* Tab 1: Outlet Item — storefront search + current outlet items */}
      {tabValue === 1 && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>Add via Storefront</Typography>
          <StorefrontProductSearch
            onProductSelect={() => fetchOutletItems()}
            onMessageShow={showMessage}
          />
          <Divider sx={{ my: 4 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Current Outlet Items</Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchOutletItems}
                disabled={loadingItems}
                sx={{ mr: 1 }}
              >
                Refresh
              </Button>
              {unbinnedCount > 0 && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<InventoryIcon />}
                  onClick={handleBackfillBins}
                  disabled={loadingItems}
                  sx={{ mr: 1 }}
                >
                  分配貨架格 ({unbinnedCount})
                </Button>
              )}
              <Button
                variant="outlined"
                color="warning"
                startIcon={<LocalOfferIcon />}
                onClick={openDiscountDialog}
                disabled={loadingItems}
                sx={{ mr: 1 }}
              >
                再打折
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleRebalanceBins}
                disabled={loadingItems}
                sx={{ mr: 1 }}
              >
                重新整理貨架格
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<LockOpenIcon />}
                onClick={handleReleaseExpired}
                disabled={loadingItems}
              >
                Release Expired
              </Button>
            </Box>
          </Box>

          {loadingItems ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : outletItems.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>No outlet items found.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Original Price</TableCell>
                    <TableCell>Outlet Price</TableCell>
                    <TableCell>Savings</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>貨架格</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outletItems.map(item => {
                    const timeRemaining = getTimeRemaining(item.expires_at);
                    const isReserved = item.reserved_by && !timeRemaining.expired;
                    const savingsPercent = calculateSavingsPercent(item.price, item.discount_price);
                    const isShopee = item.source === 'shopee' || (item.sku && item.sku.startsWith('SPE-'));

                    return (
                      <TableRow key={item.id} sx={{
                        bgcolor: isReserved ? '#fff8e1' : undefined,
                        '&:hover': { bgcolor: isReserved ? '#fff8e5' : '#f9f9f9' }
                      }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {item.image_url && (
                              <img
                                src={item.image_url}
                                alt={item.title}
                                style={{ width: 40, height: 40, marginRight: 10, objectFit: 'contain' }}
                              />
                            )}
                            <Box>
                              <Typography variant="body2">{item.title}</Typography>
                              {item.pending_order_count > 0 && (
                                <Tooltip
                                  title={
                                    <Box>
                                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                        Storefront orders waiting on this variant:
                                      </Typography>
                                      {(item.pending_order_ids || []).map((oid) => (
                                        <Typography key={oid} variant="caption" display="block">
                                          #{oid}
                                        </Typography>
                                      ))}
                                      {item.pending_order_count > (item.pending_order_ids || []).length && (
                                        <Typography variant="caption" display="block">
                                          + {item.pending_order_count - (item.pending_order_ids || []).length} more
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  arrow
                                >
                                  <Chip
                                    size="small"
                                    color="warning"
                                    label={`需求 ${item.pending_order_count} 張單 · ${item.pending_order_qty} 件`}
                                    sx={{ mt: 0.5, fontWeight: 600 }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {item.display_sku || item.sku}
                          {!item.display_sku && item.variant_details && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              ({item.variant_details})
                            </Typography>
                          )}
                          {isShopee && (
                            <Chip
                              label="Shopee"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>${parseFloat(item.price).toFixed(2)}</TableCell>
                        <TableCell>${parseFloat(item.discount_price).toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip
                            label={`${savingsPercent}% off`}
                            size="small"
                            color="error"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell>
                          {Number.isFinite(item.available_count)
                            ? item.available_count
                            : item.available_quantity}
                          {(item.reserved_count > 0 || item.sold_count > 0) && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              of {item.total_quantity}
                              {item.reserved_count > 0 && ` · ${item.reserved_count} held`}
                              {item.sold_count > 0 && ` · ${item.sold_count} sold`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="點擊設定 / 變更貨架格" arrow>
                            <Chip
                              size="small"
                              label={item.slot_number != null ? `📦 ${item.slot_number}` : '指定'}
                              onClick={() => handleOpenBinDialog(item)}
                              sx={{
                                cursor: 'pointer',
                                fontWeight: 700,
                                ...(item.slot_number != null
                                  ? { bgcolor: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe' }
                                  : { color: 'text.secondary', borderStyle: 'dashed' }),
                              }}
                              variant={item.slot_number != null ? 'filled' : 'outlined'}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {isReserved ? (
                            <Box>
                              <Chip
                                icon={<LockIcon />}
                                label="Reserved"
                                size="small"
                                color="warning"
                              />
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                By: {item.reserved_by}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {timeRemaining.text}
                              </Typography>
                            </Box>
                          ) : (
                            <Chip
                              label="Available"
                              size="small"
                              color="success"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {isReserved ? (
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleOpenReleaseDialog(item)}
                              title="Release reservation"
                            >
                              <LockOpenIcon />
                            </IconButton>
                          ) : (
                            <>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenEditDialog(item)}
                                title="Edit item"
                              >
                                <EditIcon />
                              </IconButton>

                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => handleOpenImageDialog(item)}
                                title="Correct images"
                              >
                                <ImageIcon />
                              </IconButton>

                              <IconButton
                                size="small"
                                color={item.pending_order_count > 0 ? 'warning' : 'default'}
                                onClick={() => handleOpenDivertDialog(item)}
                                title="Divert stock to a storefront order (lowers quantity, keeps sales history)"
                              >
                                <MoveDownIcon />
                              </IconButton>

                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenDeleteDialog(item)}
                                title="Delete item"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Tab 2: 依貨架格 — on-shelf items grouped by bin */}
      {tabValue === 2 && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h5">依貨架格</Typography>
              <Typography variant="body2" color="text.secondary">
                {binView.length} 件在架 · {binGroups.length} 個貨架格（可售 + 待出貨）
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchBinView}
              disabled={loadingBins}
            >
              Refresh
            </Button>
          </Box>

          <TextField
            fullWidth
            size="small"
            placeholder="搜尋 tag / 品名 / SKU — 找出該 tag 放在哪一格"
            value={binSearch}
            onChange={(e) => setBinSearch(e.target.value)}
            sx={{ mb: 2 }}
          />

          {loadingBins ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : binGroups.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              目前沒有已分配貨架格的在架商品。請到「Outlet Item」分頁點「分配貨架格」。
            </Alert>
          ) : filteredBinGroups.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              找不到符合「{binSearch}」的商品。
            </Alert>
          ) : (
            filteredBinGroups.map(([bin, items]) => {
              const avail = items.reduce((n, it) => n + (it.available_count || 0), 0);
              const pending = items.reduce((n, it) => n + (it.pending_count || 0), 0);
              const held = items.reduce((n, it) => n + (it.held_count || 0), 0);
              return (
                <Paper key={bin} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={`📦 ${bin} 號格`}
                      sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 800 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {items.length} 款
                      {avail > 0 && ` · 可售 ${avail}`}
                      {pending > 0 && ` · 待出貨 ${pending}`}
                      {held > 0 && ` · 保留中 ${held}`}
                    </Typography>
                  </Box>
                  {items.map((it) => {
                    const pendingOrders = Array.isArray(it.pending_orders) ? it.pending_orders : [];
                    // Same tag lookup as the sales history: SKU number → productMetadata.tag
                    const fullSku = it.display_sku || it.sku || '';
                    const skuNumber = fullSku.split('(')[0].split(' ')[0].trim();
                    const tag = (skuNumber && productMetadata[skuNumber]?.tag) || '';
                    return (
                      <Box
                        key={it.id}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderTop: '1px solid #f0f0f0' }}
                      >
                        {it.image_url && (
                          <img
                            src={it.image_url}
                            alt={it.title}
                            style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4, border: '1px solid #e0e0e0' }}
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>{it.title}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">
                              {it.display_sku || it.sku}{it.variant_details ? ` · ${it.variant_details}` : ''}
                            </Typography>
                            {tag && (
                              <Chip
                                label={tag}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                              />
                            )}
                          </Box>
                        </Box>
                        {it.available_count > 0 && (
                          <Chip size="small" color="success" label={`可售 ${it.available_count}`} />
                        )}
                        {it.pending_count > 0 && (
                          <Tooltip
                            arrow
                            title={
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>待出貨訂單：</Typography>
                                {pendingOrders.map((po, i) => (
                                  <Typography key={i} variant="caption" display="block">
                                    #{po.order_id}{po.customer ? ` · ${po.customer}` : ''}
                                  </Typography>
                                ))}
                              </Box>
                            }
                          >
                            <Chip size="small" color="warning" label={`待出貨 ${it.pending_count}`} />
                          </Tooltip>
                        )}
                        {it.held_count > 0 && (
                          <Tooltip title="有客人結帳中（保留 2 小時）— 實體仍在架上" arrow>
                            <Chip size="small" color="info" variant="outlined" label={`保留中 ${it.held_count}`} />
                          </Tooltip>
                        )}
                        {it.available_count > 0 && (
                          <Tooltip title="架上找不到（盤損移除，釋出貨架格）" arrow>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleWriteOffMissing(it)}
                            >
                              <RemoveShoppingCartIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })}
                </Paper>
              );
            })
          )}
        </Paper>
      )}

      {/* Tab 3: 待處理 — ghost units to relist + garments to move to receiving */}
      {tabValue === 3 && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h5">待處理</Typography>
              <Typography variant="body2" color="text.secondary">
                架上找得到、但系統已看不到的商品，以及調撥後待搬移的商品
              </Typography>
            </Box>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchReconcile} disabled={loadingReconcile}>
              Refresh
            </Button>
          </Box>

          {loadingReconcile ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              {/* Section 1: diverted garments awaiting physical move to receiving */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">
                  調撥待移（{diversionMoves.filter((m) => !m.moved).length}）
                </Typography>
                <FormControlLabel
                  control={<Switch size="small" checked={showMovedDiversions} onChange={toggleShowMovedDiversions} />}
                  label="顯示已搬移（可取消勾選）"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                這些商品已從出清調撥給正價訂單，實體需要從「出清架」搬到「收貨架」。從左邊的「原出清格」把衣服拿出來、放到右邊的「收貨格」，搬好後點右邊的空框打勾。橘色標記代表沒有明確的收貨格號（手動調撥或單件免格），需人工確認去向。
              </Typography>
              {diversionMoves.length === 0 ? (
                <Alert severity="success" sx={{ mb: 4 }}>近期沒有待搬移的調撥商品。</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>商品</TableCell>
                        <TableCell>數量</TableCell>
                        <TableCell>原出清格</TableCell>
                        <TableCell>收貨格</TableCell>
                        <TableCell>目標訂單</TableCell>
                        <TableCell>調撥日</TableCell>
                        <TableCell align="center">已搬移</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {diversionMoves.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={row.moved
                            ? { opacity: 0.55, bgcolor: '#f9fafb' }
                            : (row.needs_attention ? { bgcolor: '#fff7ed' } : undefined)}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {row.image_url && (
                                <img
                                  src={row.image_url}
                                  alt={row.title}
                                  style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4, border: '1px solid #e0e0e0', flexShrink: 0 }}
                                />
                              )}
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" noWrap>{row.title || '—'}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {row.display_sku || row.sku} · {row.variant_details}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{row.quantity}</TableCell>
                          <TableCell>
                            {row.outlet_slot != null
                              ? <Chip size="small" label={`出清 ${row.outlet_slot} 號格`} sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 700 }} />
                              : <Chip size="small" label="—" variant="outlined" />}
                          </TableCell>
                          <TableCell>
                            {row.receiving_slot != null
                              ? <Chip size="small" label={`收貨 ${row.receiving_slot} 號格`} sx={{ bgcolor: '#ecfdf5', color: '#065f46', fontWeight: 700 }} />
                              : <Chip size="small" color="warning" label="需確認" />}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{row.storefront_order_id || '（無訂單 / 手動調撥）'}</Typography>
                          </TableCell>
                          <TableCell><Typography variant="caption">{row.created_at ? new Date(row.created_at).toLocaleDateString('zh-TW') : '—'}</Typography></TableCell>
                          <TableCell align="center">
                            <Tooltip title={row.moved ? '已搬移 — 點一下取消勾選' : '搬到收貨架後，點一下打勾'} arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  color={row.moved ? 'success' : 'default'}
                                  disabled={movingId === row.id}
                                  onClick={() => handleMarkMoved(row, !row.moved)}
                                >
                                  {row.moved ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Section 2: ghost units on the shelf but hidden from the store */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                架上幽靈商品（{reconcileItems.length}）
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                這些商品的訂單已失效（未取貨 / 取消 / 已隱藏）但尚未出貨。若實體還在貨架上，
                點「退回架上」會取消佔用、重新上架並保留貨架格；若實體已不在（真的沒貨），
                改點「缺件移除」會從清單移除並釋放貨架格，但不會重新上架販售。
              </Typography>
              {reconcileItems.length === 0 ? (
                <Alert severity="success">近期沒有需要退回架上的幽靈商品。</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>商品</TableCell>
                        <TableCell>貨架格</TableCell>
                        <TableCell>原因</TableCell>
                        <TableCell>訂單</TableCell>
                        <TableCell>售出日</TableCell>
                        <TableCell align="right">動作</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reconcileItems.map((row) => {
                        const key = `${row.order_id}-${row.outlet_item_id}`;
                        const reasonLabel = { hidden: '已隱藏', cancelled: '已取消', cod_noshow: '貨到付款未取', other: '其他' }[row.reason] || row.reason;
                        const reasonColor = { hidden: 'default', cancelled: 'error', cod_noshow: 'warning', other: 'default' }[row.reason] || 'default';
                        return (
                          <TableRow key={key}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {row.image_url && (
                                  <img
                                    src={row.image_url}
                                    alt={row.title}
                                    style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4, border: '1px solid #e0e0e0', flexShrink: 0 }}
                                  />
                                )}
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" noWrap>{row.title || '—'}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {row.display_sku || row.sku} · {row.variant_details}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {row.slot_number != null
                                ? <Chip size="small" label={`📦 ${row.slot_number} 號格`} sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 700 }} />
                                : <Chip size="small" label="未分配" variant="outlined" />}
                            </TableCell>
                            <TableCell><Chip size="small" color={reasonColor} label={reasonLabel} /></TableCell>
                            <TableCell><Typography variant="caption">{row.order_id}</Typography></TableCell>
                            <TableCell><Typography variant="caption">{row.sold_at ? new Date(row.sold_at).toLocaleDateString('zh-TW') : '—'}</Typography></TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                disabled={relistingKey === key || retiringKey === key}
                                onClick={() => handleRelistGhost(row)}
                              >
                                {relistingKey === key ? '處理中…' : '退回架上'}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                sx={{ ml: 1 }}
                                disabled={relistingKey === key || retiringKey === key}
                                onClick={() => handleRetireGhost(row)}
                              >
                                {retiringKey === key ? '處理中…' : '缺件移除'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Paper>
      )}

      {/* 再打折 — blanket discount across every listed outlet item */}
      <Dialog
        open={discountDialogOpen}
        onClose={() => !discountBusy && setDiscountDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>再打折 — 全部出清商品</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            對「目前上架中」的出清商品再降價：新售價 = 現售價 × (100 − 折扣%)，
            低於最低價時以最低價為準，小數無條件捨去。
            已售完、全數被保留、或正在結帳中的商品不會被調整。
          </DialogContentText>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={6} sm={4}>
              <TextField
                label="再折 %"
                type="number"
                fullWidth
                value={discountPercent}
                onChange={(e) => { setDiscountPercent(e.target.value); setDiscountPreview(null); }}
                inputProps={{ min: 1, max: 99 }}
                helperText="15 = 打 85 折"
                autoFocus
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="最低價 (NT$)"
                type="number"
                fullWidth
                value={discountFloor}
                onChange={(e) => { setDiscountFloor(e.target.value); setDiscountPreview(null); }}
                inputProps={{ min: 0 }}
                helperText="不會低於此價"
              />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="outlined"
                onClick={runDiscountPreview}
                disabled={discountBusy || !discountPercent}
                fullWidth
              >
                {discountBusy ? '計算中…' : '試算'}
              </Button>
            </Grid>
          </Grid>

          {discountPreview && (
            <>
              <Alert severity={discountPreview.matched ? 'warning' : 'info'} sx={{ mb: 2 }}>
                {discountPreview.matched
                  ? `${discountPreview.matched} 件會調降 — 架上總值 NT$${Math.round(discountPreview.valueBefore).toLocaleString()} → NT$${Math.round(discountPreview.valueAfter).toLocaleString()}（以可售件數計）`
                  : '沒有商品會被調整（可能都已到最低價）。'}
              </Alert>
              {discountPreview.matched > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>商品</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell align="right">可售</TableCell>
                        <TableCell align="right">現售價</TableCell>
                        <TableCell align="right">新售價</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {discountPreview.items.map((it) => (
                        <TableRow key={it.id}>
                          <TableCell sx={{ maxWidth: 320 }}>
                            <Typography variant="body2" noWrap title={it.title}>{it.title}</Typography>
                            {it.variant_details && it.variant_details !== 'Default' && (
                              <Typography variant="caption" color="text.secondary">{it.variant_details}</Typography>
                            )}
                          </TableCell>
                          <TableCell>{it.sku}</TableCell>
                          <TableCell align="right">{it.available_count}</TableCell>
                          <TableCell align="right">${Number(it.before_price).toFixed(0)}</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                            ${Number(it.after_price).toFixed(0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscountDialogOpen(false)} disabled={discountBusy}>取消</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={applyBulkDiscount}
            disabled={discountBusy || !discountPreview?.matched}
          >
            {discountBusy ? '處理中…' : `套用${discountPreview?.matched ? `（${discountPreview.matched} 件）` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 架上找不到 (write-off) quantity dialog */}
      <Dialog open={!!writeOffItem} onClose={() => !writingOff && setWriteOffItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>架上找不到 — 移除數量</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {writeOffItem?.title}
            <br />
            <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
              {writeOffItem?.display_sku || writeOffItem?.sku}
              {writeOffItem?.variant_details ? ` · ${writeOffItem.variant_details}` : ''}
              {writeOffItem?.slot_number != null ? ` · 📦 ${writeOffItem.slot_number} 號格` : ''}
            </Box>
          </DialogContentText>
          <TextField
            label={`移除數量（可售 ${writeOffItem?.available_count ?? 0}）`}
            type="number"
            fullWidth
            value={writeOffQty}
            onChange={(e) => setWriteOffQty(e.target.value)}
            inputProps={{ min: 1, max: writeOffItem?.available_count ?? 1 }}
            autoFocus
            helperText="移除架上找不到的件數；剩下的仍會在販售清單。全部移除時會釋出貨架格（盤損，不記為銷售）。"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWriteOffItem(null)} disabled={writingOff}>取消</Button>
          <Button variant="contained" color="error" onClick={confirmWriteOff} disabled={writingOff}>
            {writingOff ? '處理中…' : '移除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shelf-bin (貨架格) Dialog */}
      <Dialog
        open={binDialogOpen}
        onClose={() => setBinDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>設定貨架格</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {currentItem?.title}
          </DialogContentText>
          <TextField
            label="貨架格號（留空 = 清除）"
            type="number"
            fullWidth
            value={binValue}
            onChange={(e) => setBinValue(e.target.value)}
            inputProps={{ min: 1 }}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveBin(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAutoBin} disabled={savingBin}>自動分配</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setBinDialogOpen(false)} disabled={savingBin}>取消</Button>
          <Button variant="contained" onClick={handleSaveBin} disabled={savingBin}>儲存</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
      >
        <DialogTitle>Edit Outlet Item</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update details for: {currentItem?.title}
          </DialogContentText>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Original Price"
                type="number"
                fullWidth
                value={currentItem?.price || ''}
                disabled
                InputProps={{
                  startAdornment: <Box component="span" sx={{ mr: 1 }}>$</Box>
                }}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Outlet Price"
                type="number"
                fullWidth
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                InputProps={{
                  startAdornment: <Box component="span" sx={{ mr: 1 }}>$</Box>
                }}
                required
                error={editPrice && (parseFloat(editPrice) <= 0 || parseFloat(editPrice) >= parseFloat(currentItem?.price || 0))}
                helperText={
                  editPrice && parseFloat(editPrice) >= parseFloat(currentItem?.price || 0)
                    ? "Outlet price must be lower than original price"
                    : editPrice && parseFloat(editPrice) <= 0
                      ? "Price must be greater than zero"
                      : ""
                }
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Quantity"
                type="number"
                fullWidth
                value={editQuantity}
                onChange={(e) => setEditQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateItem}
            variant="contained"
            color="primary"
            disabled={
              loadingItems ||
              !editPrice ||
              parseFloat(editPrice) <= 0 ||
              (currentItem && parseFloat(editPrice) >= parseFloat(currentItem.price))
            }
          >
            {loadingItems ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this item from the outlet?
            This action cannot be undone.
          </DialogContentText>

          {currentItem && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2">{currentItem.title}</Typography>
              <Typography variant="body2">SKU: {currentItem.sku}</Typography>
              <Typography variant="body2">
                Price: ${parseFloat(currentItem.discount_price).toFixed(2)}
                (was ${parseFloat(currentItem.price).toFixed(2)})
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteItem}
            variant="contained"
            color="error"
            disabled={loadingItems}
          >
            {loadingItems ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Divert-to-Storefront Dialog */}
      <Dialog
        open={divertDialogOpen}
        onClose={() => setDivertDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Divert to Storefront Order</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Reduce this outlet item's stock so the unit(s) can fulfil a waiting
            order (storefront or Shopee) at full price. This keeps the item's row and
            sales history intact — unlike Delete. When the last available unit is
            diverted, it drops off the outlet store automatically.
          </DialogContentText>

          {currentItem && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2">{currentItem.title}</Typography>
              <Typography variant="body2">
                SKU: {currentItem.display_sku || currentItem.sku}
                {currentItem.variant_details ? ` · ${currentItem.variant_details}` : ''}
              </Typography>
              <Typography variant="body2">
                Available now:{' '}
                {Number.isFinite(currentItem.available_count)
                  ? currentItem.available_count
                  : currentItem.available_quantity}
                {currentItem.total_quantity ? ` of ${currentItem.total_quantity}` : ''}
              </Typography>
              {currentItem.pending_order_count > 0 && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                  需求 {currentItem.pending_order_count} 張單 · {currentItem.pending_order_qty} 件 waiting
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TextField
              label="Quantity to divert"
              type="number"
              fullWidth
              value={divertQuantity}
              onChange={(e) => {
                const max = currentItem
                  ? (Number.isFinite(currentItem.available_count)
                      ? currentItem.available_count
                      : currentItem.available_quantity)
                  : 1;
                const v = Math.max(1, Math.min(max || 1, parseInt(e.target.value) || 1));
                setDivertQuantity(v);
              }}
              inputProps={{ min: 1 }}
            />
            {currentItem && (() => {
              const max = Number.isFinite(currentItem.available_count)
                ? currentItem.available_count
                : currentItem.available_quantity;
              return max > 1 ? (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={() => setDivertQuantity(max)}
                >
                  全部 ({max})
                </Button>
              ) : null;
            })()}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={divertFulfill}
                onChange={(e) => setDivertFulfill(e.target.checked)}
              />
            }
            label="自動分配給最舊的待收訂單（標記收到 + 備註「出清調撥」）"
            sx={{ mb: 1, display: 'block' }}
          />

          {/* Preview: which oldest orders this batch divert will fill */}
          {divertFulfill && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #eee' }}>
              {divertMatchesLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">載入待收訂單…</Typography>
                </Box>
              ) : divertMatches.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  目前沒有等待此商品的訂單（官網或蝦皮）。
                </Typography>
              ) : (
                <>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    將分配給最舊的 {Math.min(parseInt(divertQuantity) || 1, divertMatches.length)} 張（共 {divertMatches.length} 張等待）：
                  </Typography>
                  {divertMatches.map((m, idx) => {
                    const willFill = idx < (parseInt(divertQuantity) || 1);
                    const d = m.order_date ? new Date(m.order_date).toLocaleDateString() : '';
                    return (
                      <Box
                        key={m.order_id}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: willFill ? 1 : 0.4 }}
                      >
                        <Chip
                          size="small"
                          label={willFill ? '✓ 收到' : '等待'}
                          color={willFill ? 'success' : 'default'}
                          variant={willFill ? 'filled' : 'outlined'}
                          sx={{ height: 18, fontSize: 11 }}
                        />
                        <Chip
                          size="small"
                          label={m.source === 'shopee' ? '蝦皮' : '官網'}
                          color={m.source === 'shopee' ? 'warning' : 'primary'}
                          variant="outlined"
                          sx={{ height: 18, fontSize: 11 }}
                        />
                        <Typography variant="caption">
                          {m.order_id}{d ? ` · ${d}` : ''}
                        </Typography>
                      </Box>
                    );
                  })}
                </>
              )}
            </Box>
          )}

          <TextField
            label="Storefront order ID (audit only)"
            fullWidth
            value={divertOrderId}
            onChange={(e) => setDivertOrderId(e.target.value)}
            placeholder="ORD-xxxxx"
            helperText={
              divertFulfill
                ? 'Auto-fulfil is on — the oldest waiting order(s) are picked automatically; this field is just for your records.'
                : 'For your audit trail — not required.'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDivertDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDivertItem}
            variant="contained"
            color="warning"
            disabled={loadingItems || !divertQuantity || parseInt(divertQuantity) < 1}
          >
            {loadingItems ? <CircularProgress size={24} /> : 'Divert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Correction Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
      >
        <DialogTitle>Correct Item Images</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the Item ID to fetch correct images for:
          </DialogContentText>

          {currentItem && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2">{currentItem.title}</Typography>
              <Typography variant="body2">Current SKU: {currentItem.sku}</Typography>
              {currentItem.image_url && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src={currentItem.image_url}
                    alt="Current Item"
                    style={{ maxHeight: 150, maxWidth: '100%', objectFit: 'contain' }}
                  />
                </Box>
              )}
            </Box>
          )}

          <TextField
            label="Item ID"
            fullWidth
            value={imageItemId}
            onChange={(e) => setImageItemId(e.target.value)}
            placeholder="Enter Item ID"
            variant="outlined"
            sx={{ mb: 2 }}
          />

          {previewImages.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Preview Images:</Typography>
              <Grid container spacing={2}>
                {previewImages.map((img) => (
                  <Grid item xs={4} key={img.id}>
                    <img
                      src={img.src}
                      alt={`Preview ${img.id}`}
                      style={{
                        width: '100%',
                        height: 120,
                        objectFit: 'cover',
                        border: '1px solid #ddd',
                        borderRadius: 4
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handlePreviewImages(currentItem.id, imageItemId)}
            variant="outlined"
            disabled={!imageItemId || correctingImages}
          >
            {correctingImages ? <CircularProgress size={24} /> : 'Preview Images'}
          </Button>
          <Button onClick={() => setImageDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCorrectImages}
            variant="contained"
            color="primary"
            disabled={correctingImages || !imageItemId}
          >
            {correctingImages ? <CircularProgress size={24} /> : 'Update Images'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release Reservation Dialog */}
      <Dialog
        open={releaseDialogOpen}
        onClose={() => setReleaseDialogOpen(false)}
      >
        <DialogTitle>Release Reservation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to release this reserved item?
            This will make it available for other customers.
          </DialogContentText>

          {currentItem && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#fff8e1', borderRadius: 1 }}>
              <Typography variant="subtitle2">{currentItem.title}</Typography>
              <Typography variant="body2">Reserved by: {currentItem.reserved_by}</Typography>
              <Typography variant="body2">
                Reserved until: {formatDate(currentItem.expires_at)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReleaseReservation}
            variant="contained"
            color="warning"
            disabled={loadingItems}
          >
            {loadingItems ? <CircularProgress size={24} /> : 'Release Reservation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={messageType}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OutletManager;